import {
  type SecretListItem,
  type SecretListQuery,
  SecretProviderTypeSchema,
} from '@utazzo/shared';
import { UzButton, UzDialog, UzStatusBadge, type UzDropdownMenuItem } from '@utazzo/ui';
import { Copy, Eye } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { SecretFormDrawer } from '../components/SecretFormDrawer';
import { useDeleteSecret, useRevealSecret, useSecretsList } from '../use-secrets';

import { type DataGridColumn, type DataGridFilter } from '@/components/data-grid';
import { ListScreen, type ListScreenColumns } from '@/components/list-screen';

const STATUS_OPTIONS = [
  { value: '', label: 'Active + inactive' },
  { value: 'active', label: 'Active only' },
  { value: 'inactive', label: 'Inactive only' },
] as const;

const PROVIDER_TYPE_FILTER = [
  { value: '', label: 'All provider types' },
  ...SecretProviderTypeSchema.options.map((v) => ({
    value: v,
    label: v.charAt(0) + v.slice(1).toLowerCase(),
  })),
];

const MIN_SEARCH_LENGTH = 3;

const FILTERS: ReadonlyArray<DataGridFilter> = [
  {
    id: 'providerType',
    label: 'Provider type',
    options: PROVIDER_TYPE_FILTER,
    widthClass: 'w-52',
  },
  {
    id: 'status',
    label: 'Status',
    options: STATUS_OPTIONS as ReadonlyArray<{ value: string; label: string }>,
    widthClass: 'w-48',
  },
];

export const SecretsListPage = (): React.JSX.Element => {
  const [revealed, setRevealed] = useState<{ key: string; value: string } | null>(null);

  const deleteSecret = useDeleteSecret();
  const revealSecret = useRevealSecret();

  const columns: ListScreenColumns<SecretListItem> = ({ state }) => {
    const cols: ReadonlyArray<DataGridColumn<SecretListItem>> = [
      {
        id: 'serial',
        header: 'S.No',
        width: '64px',
        cell: (_row, index) => (
          <span className="text-zinc-500">{(state.page - 1) * state.pageSize + index + 1}.</span>
        ),
      },
      {
        id: 'key',
        header: 'Key',
        sortable: true,
        cell: (row) => (
          <div className="flex flex-col">
            <span className="font-medium text-zinc-900">{row.label}</span>
            <span className="font-mono text-xs text-zinc-500">{row.key}</span>
          </div>
        ),
      },
      {
        id: 'provider',
        header: 'Provider',
        cell: (row) => (
          <span>
            {row.provider ?? '-'}
            {row.providerType ? ` · ${row.providerType.toLowerCase()}` : ''}
          </span>
        ),
      },
      {
        id: 'kind',
        header: 'Kind',
        cell: (row) => (
          <span className="text-zinc-600">{row.kind.replace(/_/g, ' ').toLowerCase()}</span>
        ),
      },
      {
        id: 'environment',
        header: 'Env',
        width: '80px',
        cell: (row) => <span>{row.environment.toLowerCase()}</span>,
      },
      {
        id: 'value',
        header: 'Value',
        cell: (row) => (
          <span className="font-mono text-zinc-400">{row.valuePreview ?? '••••'}</span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: (row) => <UzStatusBadge status={row.isActive ? 'active' : 'inactive'} />,
      },
    ];
    return cols;
  };

  return (
    <ListScreen<SecretListItem, SecretListQuery>
      title="Secrets Manager"
      description="Third-party provider credentials and environment values, encrypted at rest. Revealing a value is audited."
      useList={useSecretsList}
      defaultSort={{ by: 'createdAt', dir: 'desc' }}
      minSearchLength={MIN_SEARCH_LENGTH}
      toQuery={(state, base) => {
        const status = state.filters.status;
        const providerType = state.filters.providerType;
        return {
          ...base,
          ...(providerType
            ? { providerType: providerType as SecretListQuery['providerType'] }
            : {}),
          ...(status === 'active' ? { isActive: true } : {}),
          ...(status === 'inactive' ? { isActive: false } : {}),
        };
      }}
      columns={columns}
      filters={FILTERS}
      getRowId={(row) => row.id}
      createButton={{ label: 'Add secret' }}
      renderDrawer={({ open, mode, row, close }) => (
        <SecretFormDrawer
          open={open}
          onOpenChange={(o) => {
            if (!o) close();
          }}
          mode={mode}
          secret={row}
        />
      )}
      rowActions={{
        edit: true,
        extra: (row): UzDropdownMenuItem[] => [
          {
            type: 'item',
            label: 'Reveal value',
            icon: <Eye className="h-4 w-4" />,
            onSelect: () =>
              revealSecret.mutate(row.id, {
                onSuccess: (data) => setRevealed({ key: data.key, value: data.value }),
              }),
          },
        ],
        delete: {
          onDelete: (row) => deleteSecret.mutateAsync(row.id),
          confirm: (row) => ({
            title: 'Delete this secret?',
            description: `${row.key} will be soft-deleted. Provider clients will stop receiving it immediately.`,
          }),
        },
      }}
      overlays={
        <UzDialog
          open={revealed !== null}
          onOpenChange={(open) => !open && setRevealed(null)}
          title="Revealed secret"
          description={revealed ? `${revealed.key} - this view was recorded in the audit log.` : ''}
          footer={
            <div className="flex justify-end gap-2">
              <UzButton
                variant="ghost"
                onClick={() => {
                  if (revealed) {
                    void navigator.clipboard?.writeText(revealed.value);
                    toast.success('Copied to clipboard');
                  }
                }}
              >
                <Copy className="mr-2 h-4 w-4" />
                Copy
              </UzButton>
              <UzButton onClick={() => setRevealed(null)}>Done</UzButton>
            </div>
          }
        >
          <pre className="overflow-x-auto rounded-md bg-zinc-100 p-3 font-mono text-sm break-all whitespace-pre-wrap text-zinc-900">
            {revealed?.value}
          </pre>
        </UzDialog>
      }
      copy={{
        searchPlaceholder: 'Search by key, label, or provider (min 3 characters)',
        emptyTitle: 'No secrets match these filters',
        emptyDescription: 'Adjust the filters or clear the search box.',
        errorTitle: 'Failed to load secrets',
        errorDescription: 'The server returned an error. Try refreshing or check your network.',
      }}
    />
  );
};
